import { Injectable } from '@angular/core';
import { AclService } from '@y42/acl';
import {
  AclOrgPermission,
  AclOrgUserPermission,
  AclPermission,
  AclPermissionDetail,
  AclPermissionsRecord,
  AclResource,
  AclResourceType,
} from '@y42/acl/models';
import { ErrorHandlerService } from '@y42/error-handling';
import { OrgQuery } from '@y42/org/state';
import { UserUIModel } from '@y42/user/models';
import { isNullish } from '@y42/util/nullish';
import { Observable, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class OrgUserAclService {
  constructor(
    private readonly aclService: AclService,
    private readonly orgQuery: OrgQuery,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {}

  enumerateOrgUsers({ permissions }: { permissions: AclPermission[] }): Observable<AclResource[]> {
    return this.aclService.enumerateResources({
      resourceType: AclResourceType.User,
      permissions,
    });
  }

  checkUserPermission({
    userId,
    permission,
  }: {
    userId: UserUIModel['id'];
    permission: AclOrgUserPermission;
  }): Observable<AclPermissionDetail> {
    return this.aclService.checkPermission({
      resourceType: AclResourceType.User,
      resourceId: `${userId}`,
      permission,
    });
  }

  checkUserPermissions({
    userId,
    permissions,
  }: {
    userId: UserUIModel['id'];
    permissions: AclOrgUserPermission[];
    spaceId?: string;
  }): Observable<AclPermissionsRecord> {
    return this.aclService.checkPermissions({
      resourceType: AclResourceType.User,
      resourceId: `${userId}`,
      permissions,
    });
  }

  checkCanCreateUsers(): Observable<AclPermissionDetail> {
    const orgId = this.orgQuery.getActive()?.id;
    if (isNullish(orgId)) {
      this.errorHandlerService.error(`[OrgUserAclService.checkCanCreateUsers]: nullish orgId`);
      return of({ allowed: false });
    }

    return this.aclService.checkPermission({
      resourceType: AclResourceType.Org,
      resourceId: `${orgId}`,
      // Check for any grant permission, eg: GrantGuest
      permission: AclOrgPermission.GrantGuest,
    });
  }
}
